// src/pages/LoginPage.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { loginUser } from "../redux/slices/authSlice";
import {
  User, Lock, Eye, EyeOff, Loader2, ShieldCheck, AlertCircle,
  KeyRound, LogIn, Languages, CheckCircle2, Package, Truck, MapPin, 
  Zap, Globe, TrendingUp, Sparkles, Info, ArrowRight, Award
} from "lucide-react";
import SOURCE_MAP from "../config/sourceMap";

export default function LoginPage({ backgroundImage = "/logo.png", source = "wayuvega" }) {
  const brandSource = source || "wayuvega";
  const brand = SOURCE_MAP[brandSource] || SOURCE_MAP.wayuvega;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isAuthenticated, loading, error, token, merchantId } = useSelector((state) => state.auth);

  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [capsLock, setCapsLock] = useState(false);
  const [remember, setRemember] = useState(true);
  const [formError, setFormError] = useState("");
  const [strength, setStrength] = useState("Weak");
  const [locale, setLocale] = useState("en");
  const [activeFeature, setActiveFeature] = useState(0);
  const [showSignupMessage, setShowSignupMessage] = useState(false);
  const [loginSuccess, setLoginSuccess] = useState(false);

  const usernameRef = useRef(null);

  const canSubmit = useMemo(
    () => username.trim().length >= 3 && password.trim().length >= 6 && !loading,
    [username, password, loading]
  );

  // Persist source
  useEffect(() => {
    try {
      localStorage.setItem("source", brandSource);
    } catch (e) {
      // ignore
    }
  }, [brandSource]);

  // Set favicon and title
  useEffect(() => {
    document.querySelector("link[rel='icon']").href = "/wayuvega-favicon.png";
    document.title = "Wayuvega - Login";
  }, []);

  // Post-login bootstrap with success animation
  useEffect(() => {
    if (isAuthenticated && token && merchantId) {
      setLoginSuccess(true);
      
      setTimeout(() => {
        (async () => {
          try {
            const carrierUrl = `${process.env.REACT_APP_OMS_BASE_URL}${process.env.REACT_APP_ALLOCATED_CARRIER_DETAILS}`;
            const cRes = await axios.post(carrierUrl, { merchant_id: merchantId }, { headers: { Authorization: token } });
            localStorage.setItem("allocatedCarriers", JSON.stringify(cRes.data?.allocated_carrier_details || []));

            const parentClientId = localStorage.getItem("parentClientId");
            const profileUrl = `${process.env.REACT_APP_POCAS_BASE_URL}${process.env.REACT_APP_GET_PROFILE}${parentClientId}`;
            const pRes = await axios.get(profileUrl, { headers: { Authorization: token } });
            localStorage.setItem("profileData", JSON.stringify(pRes.data));
          } catch (e) {
            console.error("Post-login fetch error:", e);
          } finally {
            navigate("/all-shipments", { replace: true });
          }
        })();
      }, 1200);
    }
  }, [isAuthenticated, token, merchantId, navigate]);

  // Focus username
  useEffect(() => {
    requestAnimationFrame(() => usernameRef.current?.focus());
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    if (!canSubmit) return;
    dispatch(loginUser({ username: username.trim(), password, source: 'wayuvega' }));
  };

  const togglePassword = () => setShowPassword((v) => !v);
  const onKeyEvent = (e) => setCapsLock(e.getModifierState && e.getModifierState("CapsLock"));

  useEffect(() => {
    if (password.length < 6) setStrength("Weak");
    else if (/[A-Z]/.test(password) && /[0-9]/.test(password) && /[!@#$%^&*]/.test(password)) setStrength("Strong");
    else if (/[A-Z]/.test(password) && /[0-9]/.test(password)) setStrength("Medium");
    else setStrength("Weak");
  }, [password]);

  const features = useMemo(() => [
    {
      icon: <Sparkles className="h-6 w-6" />,
      title: "Smart Route Optimization",
      description: "AI-powered routing for faster domestic deliveries across India",
      gradient: "from-purple-500 to-pink-500"
    },
    {
      icon: <TrendingUp className="h-6 w-6" />,
      title: "Real-Time Tracking",
      description: "Live shipment visibility from pickup to doorstep delivery",
      gradient: "from-blue-500 to-cyan-500"
    },
    {
      icon: <Zap className="h-6 w-6" />,
      title: "Rapid Integration",
      description: "Seamlessly connect with your existing systems in minutes",
      gradient: "from-orange-500 to-yellow-500"
    },
    {
      icon: <Globe className="h-6 w-6" />,
      title: "Pan-India Coverage",
      description: "Extensive network covering 25,000+ pin codes nationwide",
      gradient: "from-green-500 to-emerald-500"
    }
  ], []);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % features.length);
    }, 3500);
    return () => clearInterval(interval);
  }, [features.length]);

  const handleSignupClick = () => {
    setShowSignupMessage(true);
    setTimeout(() => setShowSignupMessage(false), 8000);
  };

  // Generic error message for better UX
  const displayError = error || formError;
  const errorMessage = displayError 
    ? "Invalid username or password. Please check your credentials and try again." 
    : "";

  return (
    <div className="min-h-screen relative overflow-hidden bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      
      {/* Enhanced animated gradient orbs */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div 
          className="absolute -top-40 -left-40 h-96 w-96 rounded-full blur-3xl opacity-20 bg-gradient-to-br from-blue-400 to-cyan-300"
          style={{ animation: 'float1 25s ease-in-out infinite' }}
        />
        <div 
          className="absolute -bottom-40 -right-40 h-[36rem] w-[36rem] rounded-full blur-3xl opacity-20 bg-gradient-to-br from-indigo-400 to-purple-300"
          style={{ animation: 'float2 30s ease-in-out infinite' }}
        />
        <div 
          className="absolute top-1/3 right-1/4 h-80 w-80 rounded-full blur-3xl opacity-15 bg-gradient-to-br from-pink-300 to-rose-200"
          style={{ animation: 'float3 20s ease-in-out infinite' }}
        />
        <div 
          className="absolute bottom-1/3 left-1/4 h-72 w-72 rounded-full blur-3xl opacity-15 bg-gradient-to-br from-cyan-300 to-blue-200"
          style={{ animation: 'float1 28s ease-in-out infinite reverse' }}
        />
      </div>

      {/* Modern grid pattern */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(rgba(99,102,241,0.02)_1px,transparent_1px),linear-gradient(90deg,rgba(99,102,241,0.02)_1px,transparent_1px)] bg-[size:80px_80px]" />
      
      {/* Noise texture overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.015] mix-blend-soft-light" 
        style={{ 
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\'/%3E%3C/svg%3E")'
        }}
      />

      {/* Enhanced Animated Delivery Route */}
      <svg className="pointer-events-none absolute inset-0 w-full h-full opacity-30" style={{ zIndex: 1 }}>
        <defs>
          <linearGradient id="routeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" style={{ stopColor: '#3b82f6', stopOpacity: 0 }} />
            <stop offset="30%" style={{ stopColor: '#3b82f6', stopOpacity: 0.8 }} />
            <stop offset="70%" style={{ stopColor: '#6366f1', stopOpacity: 0.8 }} />
            <stop offset="100%" style={{ stopColor: '#6366f1', stopOpacity: 0 }} />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="2.5" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
        </defs>
        <path
          d="M 50 80 Q 200 120, 350 60 T 650 100 T 950 80"
          stroke="url(#routeGradient)"
          strokeWidth="3"
          fill="none"
          strokeDasharray="10,15"
          filter="url(#glow)"
          style={{ animation: 'dashMove 30s linear infinite' }}
        />
      </svg>

      {/* Enhanced Floating Packages */}
      <div className="pointer-events-none absolute inset-0" style={{ zIndex: 2 }}>
        {[...Array(8)].map((_, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animation: `floatPackage ${15 + Math.random() * 10}s ease-in-out infinite`,
              animationDelay: `${i * 2}s`,
              opacity: 0.12
            }}
          >
            <Package 
              className="h-8 w-8 text-blue-600" 
              style={{ filter: 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.3))' }}
            />
          </div>
        ))}
      </div>

      {/* Enhanced Animated Delivery Truck */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" style={{ zIndex: 3 }}>
        <div
          className="absolute"
          style={{
            animation: 'truckDrive 25s linear infinite',
            top: '14%'
          }}
        >
          <div className="relative">
            <Truck 
              className="h-10 w-10 text-indigo-600 opacity-25" 
              style={{ filter: 'drop-shadow(0 0 10px rgba(99, 102, 241, 0.4))' }}
            />
            {/* Enhanced exhaust particles */}
            <div className="absolute -right-3 top-2">
              {[...Array(3)].map((_, i) => (
                <div
                  key={i}
                  className="absolute w-1.5 h-1.5 rounded-full bg-slate-400"
                  style={{
                    animation: `exhaust 1.5s ease-out infinite`,
                    animationDelay: `${i * 0.5}s`,
                    opacity: 0.25,
                    boxShadow: '0 0 6px rgba(148, 163, 184, 0.3)'
                  }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Animated Map Pins */}
      <div className="pointer-events-none absolute inset-0" style={{ zIndex: 2 }}>
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute"
            style={{
              left: `${20 + i * 15}%`,
              top: `${30 + (i % 2) * 40}%`,
            }}
          >
            <div style={{ animation: `pinPulse 2s ease-in-out infinite`, animationDelay: `${i * 0.3}s` }}>
              <MapPin 
                className="h-6 w-6 text-blue-500 opacity-15" 
                style={{ filter: 'drop-shadow(0 0 6px rgba(59, 130, 246, 0.3))' }}
              />
              {/* Enhanced pulse rings */}
              <div
                className="absolute inset-0 rounded-full border-2 border-blue-400"
                style={{
                  animation: `pingExpand 2s ease-out infinite`,
                  animationDelay: `${i * 0.3}s`
                }}
              />
              <div
                className="absolute inset-0 rounded-full border border-blue-300"
                style={{
                  animation: `pingExpand 2s ease-out infinite`,
                  animationDelay: `${i * 0.3 + 0.15}s`
                }}
              />
            </div>
          </div>
        ))}
      </div>

      {/* Connection Lines (animated) */}
      <svg className="pointer-events-none absolute inset-0 w-full h-full opacity-8" style={{ zIndex: 1 }}>
        <g>
          {[...Array(5)].map((_, i) => (
            <line
              key={i}
              x1={`${20 + i * 20}%`}
              y1="30%"
              x2={`${30 + i * 20}%`}
              y2="70%"
              stroke="#3b82f6"
              strokeWidth="1"
              strokeDasharray="5,5"
              style={{
                animation: `lineGlow 3s ease-in-out infinite`,
                animationDelay: `${i * 0.6}s`
              }}
            />
          ))}
        </g>
      </svg>

      <div className="relative z-10 flex items-center justify-center min-h-screen p-4">
        <div className="grid w-full max-w-6xl grid-cols-1 lg:grid-cols-2 gap-8 items-center">
          
          {/* Left: Enhanced Branding & Features */}
          <div className="hidden lg:flex flex-col space-y-10">
            
            {/* Enhanced Logo & Title */}
            <div className="space-y-10">
              <div className="flex items-center gap-3 group">
                <div className="h-14 w-14 rounded-2xl bg-gradient-to-br from-blue-600 via-blue-500 to-indigo-600 flex items-center justify-center relative overflow-hidden shadow-xl shadow-blue-500/25 group-hover:shadow-2xl group-hover:shadow-blue-500/40 transition-all duration-300">
                  <img src={backgroundImage} alt="Wayuvega" className="h-9 w-9 object-contain brightness-0 invert relative z-10" />
                  {/* Enhanced shimmer effect */}
                  <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent group-hover:translate-x-full transition-transform duration-700" style={{ animation: 'shimmer 3s ease-in-out infinite' }} />
                  {/* Rotating border */}
                  <div className="absolute inset-0 rounded-2xl opacity-50" style={{
                    background: 'conic-gradient(from 0deg, transparent, rgba(255,255,255,0.3), transparent)',
                    animation: 'spin 4s linear infinite'
                  }} />
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-900">{brand.title}</div>
                  <div className="text-sm text-blue-600 font-semibold">B2C Logistics Platform</div>
                </div>
              </div>
              
              <div className="space-y-4">
                <h1 className="text-5xl font-extrabold text-slate-900 leading-[1.1] tracking-tight">
                  B2C Logistics,
                  <br />
                  <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 bg-clip-text text-transparent animate-gradient">
                    Delivered Right
                  </span>
                </h1>
                
                <p className="text-lg text-slate-600 max-w-md leading-relaxed">
                  India's trusted B2C logistics platform. Ship faster, track smarter, and scale seamlessly across the nation.
                </p>
              </div>
            </div>

            {/* Enhanced Feature Cards */}
            <div className="space-y-3">
              {features.map((feature, idx) => (
                <div
                  key={idx}
                  className={`group p-5 rounded-2xl backdrop-blur-md border transition-all duration-500 cursor-default ${
                    idx === activeFeature
                      ? 'bg-white border-indigo-200 shadow-xl shadow-indigo-100/50 scale-[1.02] opacity-100'
                      : 'bg-white/60 border-slate-200 scale-95 opacity-60 hover:opacity-80 hover:scale-100'
                  }`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`rounded-xl p-2.5 transition-all relative overflow-hidden ${
                      idx === activeFeature
                        ? `bg-gradient-to-br ${feature.gradient} text-white shadow-md`
                        : 'bg-gradient-to-br from-slate-100 to-slate-50 text-slate-400'
                    }`}>
                      {feature.icon}
                      {idx === activeFeature && (
                        <>
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700" style={{ animation: 'shimmer 2s ease-in-out infinite' }} />
                          <div className="absolute inset-0 rounded-xl animate-ping-slow opacity-50" style={{
                            background: `linear-gradient(to bottom right, var(--tw-gradient-stops))`,
                            filter: 'blur(8px)'
                          }} />
                        </>
                      )}
                    </div>
                    <div className="flex-1">
                      <h3 className="font-bold text-slate-900 text-base mb-1 tracking-tight">
                        {feature.title}
                      </h3>
                      <p className="text-sm text-slate-600 leading-relaxed">
                        {feature.description}
                      </p>
                    </div>
                    {idx === activeFeature && (
                      <div className="opacity-0 group-hover:opacity-100 transition-opacity">
                        <ArrowRight className="h-5 w-5 text-indigo-600" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Enhanced Trust Badges */}
            <div className="flex flex-wrap gap-4 pt-4">
              {[
                { icon: ShieldCheck, text: "ISO Certified", color: "green" },
                { icon: ShieldCheck, text: "Secure Platform", color: "blue" },
                { icon: Award, text: "99.9% Uptime SLA", color: "indigo" }
              ].map((badge, idx) => {
                const Icon = badge.icon;
                return (
                  <div key={idx} className={`flex items-center gap-2 px-4 py-2 rounded-full bg-${badge.color}-50/80 border border-${badge.color}-200/50 backdrop-blur-sm group hover:shadow-md hover:scale-105 transition-all duration-300 cursor-default`}>
                    <Icon className={`h-5 w-5 text-${badge.color}-600 group-hover:scale-110 transition-transform`} style={{ animation: 'pulse 2s ease-in-out infinite', animationDelay: `${idx * 0.3}s` }} />
                    <span className={`text-sm font-medium text-${badge.color}-700`}>{badge.text}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right: Enhanced Login Form */}
          <div>
            <div className="relative">
              {/* Success Overlay */}
              {loginSuccess && (
                <div className="absolute inset-0 z-50 bg-white/95 backdrop-blur-sm flex items-center justify-center rounded-3xl animate-in fade-in zoom-in duration-500">
                  <div className="text-center">
                    <div className="relative mx-auto mb-6">
                      <div className="h-20 w-20 mx-auto rounded-full bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center shadow-2xl shadow-green-500/50">
                        <CheckCircle2 className="h-12 w-12 text-white animate-in zoom-in duration-700" />
                      </div>
                      {/* Success rings */}
                      <div className="absolute inset-0 rounded-full border-4 border-green-400 animate-ping-slow opacity-75" />
                      <div className="absolute inset-0 rounded-full border-2 border-green-300 animate-ping-slower opacity-50" />
                    </div>
                    <h3 className="text-2xl font-bold text-slate-900 mb-2">Login Successful!</h3>
                    <p className="text-slate-600">Redirecting to your dashboard...</p>
                  </div>
                </div>
              )}

              <div className="relative rounded-3xl bg-white/85 backdrop-blur-2xl border border-white/60 p-8 shadow-2xl shadow-slate-900/10">
                {/* Gradient overlays */}
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-50/40 via-transparent to-indigo-50/40 pointer-events-none" />
                <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-blue-500/5 via-transparent to-indigo-500/5 pointer-events-none" />
                
                <div className="relative">
                  <div className="text-center mb-8">
                    {/* Mobile Logo */}
                    <div className="lg:hidden mb-8 flex justify-center">
                      <div className="flex items-center gap-3">
                        <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-blue-600 to-indigo-600 flex items-center justify-center relative overflow-hidden shadow-xl shadow-blue-500/25">
                          <img src={backgroundImage} alt="Wayuvega" className="h-8 w-8 object-contain brightness-0 invert relative z-10" />
                          <div className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent" style={{ animation: 'shimmer 3s ease-in-out infinite' }} />
                        </div>
                        <div>
                          <div className="text-xl font-bold text-slate-900">{brand.title}</div>
                          <div className="text-xs text-blue-600 font-semibold">B2C Logistics Platform</div>
                        </div>
                      </div>
                    </div>
                    
                    <h2 className="text-3xl font-bold text-slate-900 mb-2 tracking-tight">Welcome Back</h2>
                    <p className="text-slate-600">Sign in to access your logistics dashboard</p>
                  </div>

                  <form onSubmit={handleSubmit} className="space-y-5">
                    
                    {/* Enhanced Username */}
                    <div className="space-y-2">
                      <label htmlFor="username" className="block text-sm font-semibold text-slate-700 ml-1">
                        Username
                      </label>
                      <div className="relative group">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        <input
                          ref={usernameRef}
                          id="username"
                          name="username"
                          type="text"
                          value={username}
                          onChange={(e) => setUsername(e.target.value)}
                          placeholder="Enter your username"
                          className="w-full pl-12 pr-4 py-3.5 rounded-xl bg-white/80 border border-slate-300 text-slate-900 placeholder-slate-400 outline-none transition-all duration-200 focus:bg-blue-50/50 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 hover:border-slate-400"
                          autoComplete="username"
                        />
                      </div>
                    </div>

                    {/* Enhanced Password */}
                    <div className="space-y-2">
                      <label htmlFor="password" className="block text-sm font-semibold text-slate-700 ml-1">
                        Password
                      </label>
                      <div className="relative group">
                        <Lock className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400 group-focus-within:text-blue-500 transition-colors" />
                        <input
                          id="password"
                          name="password"
                          type={showPassword ? "text" : "password"}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          onKeyUp={onKeyEvent}
                          placeholder="Enter your password"
                          className="w-full pl-12 pr-12 py-3.5 rounded-xl bg-white/80 border border-slate-300 text-slate-900 placeholder-slate-400 outline-none transition-all duration-200 focus:bg-blue-50/50 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10 hover:border-slate-400"
                          autoComplete="current-password"
                        />
                        <button
                          type="button"
                          onClick={togglePassword}
                          aria-label={showPassword ? "Hide password" : "Show password"}
                          className="absolute right-3 top-1/2 -translate-y-1/2 p-2 rounded-lg hover:bg-slate-100 active:bg-slate-200 transition-all duration-200"
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4 text-slate-500" />
                          ) : (
                            <Eye className="h-4 w-4 text-slate-500" />
                          )}
                        </button>
                      </div>
                    </div>

                    {/* Enhanced Strength meter */}
                    {password && (
                      <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                        <div className="h-2 w-full rounded-full bg-slate-200 overflow-hidden shadow-inner">
                          <div
                            className={`h-full transition-all duration-500 ${
                              strength === "Weak"
                                ? "w-1/3 bg-gradient-to-r from-rose-400 to-rose-500"
                                : strength === "Medium"
                                ? "w-2/3 bg-gradient-to-r from-amber-400 to-orange-500"
                                : "w-full bg-gradient-to-r from-green-400 to-emerald-500"
                            }`}
                            style={{
                              boxShadow: strength === "Strong" ? '0 0 10px rgba(16, 185, 129, 0.5)' : 'none'
                            }}
                          />
                        </div>
                        <div className="flex items-center justify-between text-xs">
                          <span
                            className={`font-semibold ${
                              strength === "Weak"
                                ? "text-rose-600"
                                : strength === "Medium"
                                ? "text-amber-600"
                                : "text-emerald-600"
                            }`}
                          >
                            Password Strength: {strength}
                          </span>
                          {capsLock && (
                            <span className="text-amber-600 flex items-center gap-1 font-medium animate-pulse">
                              <KeyRound className="h-4" /> Caps Lock is ON
                            </span>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Remember & Forgot Password */}
                    <div className="flex items-center justify-between text-sm">
                      <label className="flex items-center gap-2 text-slate-700 cursor-pointer group">
                        <input
                          type="checkbox"
                          checked={remember}
                          onChange={(e) => setRemember(e.target.checked)}
                          className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-2 focus:ring-blue-500/20 cursor-pointer transition-all"
                        />
                        <span className="font-medium group-hover:text-slate-900 transition-colors">Remember me</span>
                      </label>
                    </div>

                    {/* Enhanced Error */}
                    {errorMessage && (
                      <div className="flex items-start gap-3 p-4 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 animate-in fade-in slide-in-from-top-2 duration-300">
                        <AlertCircle className="h-5 w-5 flex-shrink-0 mt-0.5" />
                        <div>
                          <p className="font-semibold text-sm mb-0.5">Authentication Failed</p>
                          <p className="text-sm">{errorMessage}</p>
                        </div>
                      </div>
                    )}

                    {/* Enhanced Submit */}
                    <button
                      type="submit"
                      disabled={!canSubmit}
                      className={`relative w-full flex items-center justify-center gap-2 py-3.5 rounded-xl font-bold transition-all duration-300 overflow-hidden group ${
                        canSubmit
                          ? 'bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 hover:from-blue-700 hover:via-indigo-700 hover:to-purple-700 text-white shadow-xl shadow-blue-500/30 hover:shadow-2xl hover:shadow-blue-500/40 active:scale-[0.98] hover:scale-[1.02]'
                          : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      {canSubmit && !loading && (
                        <>
                          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/25 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
                          <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300" 
                            style={{
                              background: 'radial-gradient(circle at center, rgba(255,255,255,0.1) 0%, transparent 70%)'
                            }}
                          />
                        </>
                      )}
                      <span className="relative z-10 flex items-center gap-2">
                        {loading ? (
                          <>
                            <Loader2 className="h-5 w-5 animate-spin" />
                            Signing in...
                          </>
                        ) : (
                          <>
                            <LogIn className="h-5 w-5 group-hover:translate-x-0.5 transition-transform" />
                            Sign In
                            <ArrowRight className="h-5 w-5 opacity-0 -translate-x-2 group-hover:opacity-100 group-hover:translate-x-0 transition-all" />
                          </>
                        )}
                      </span>
                    </button>

                    {/* Enhanced Signup Message */}
                    {showSignupMessage && (
                      <div className="flex items-start gap-3 p-5 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 text-blue-800 animate-in fade-in slide-in-from-top-2 duration-300 shadow-lg shadow-blue-100/50">
                        <div className="p-2 rounded-lg bg-blue-100">
                          <Info className="h-5 w-5 flex-shrink-0 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-bold mb-1.5">Interested in our services?</p>
                          <p className="text-sm leading-relaxed">
                            Please reach out to us at{" "}
                            <a 
                              href="mailto:support@wayuvega.com" 
                              className="font-bold underline decoration-blue-400 decoration-2 underline-offset-2 hover:text-blue-900 hover:decoration-blue-600 transition-all"
                            >
                              support@wayuvega.com
                            </a>
                          </p>
                        </div>
                      </div>
                    )}

                    {/* Enhanced Footer links */}
                    <div className="space-y-4 pt-4 border-t border-slate-200">
                      <p className="text-center text-slate-600 text-sm">
                        Don't have an account?{" "}
                        <Link 
                          to="/merchant-signup" 
                          className="font-bold text-blue-600 hover:text-blue-700 hover:underline underline-offset-2 transition-all"
                        >
                          Sign up now
                        </Link>
                      </p>
                      
                      <div className="flex flex-wrap justify-center gap-4 text-xs text-slate-500">
                        <Link to="/terms-and-conditions" className="hover:text-slate-700 hover:underline underline-offset-2 transition-colors font-medium">
                          Terms
                        </Link>
                        <Link to="/privacy-policy" className="hover:text-slate-700 hover:underline underline-offset-2 transition-colors font-medium">
                          Privacy
                        </Link>
                        <Link to="/refund-policy" className="hover:text-slate-700 hover:underline underline-offset-2 transition-colors font-medium">
                          Refunds
                        </Link>
                        <button type="button" className="flex items-center gap-1 hover:text-slate-700 transition-colors font-medium group">
                          <Languages className="h-4 group-hover:scale-110 transition-transform" /> {locale.toUpperCase()}
                        </button>
                      </div>
                      
                      {/* Enhanced Security Badge */}
                      <div className="flex items-center justify-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-xs text-green-700 mx-auto w-fit group hover:shadow-lg hover:shadow-green-100/50 transition-all duration-300 cursor-default">
                        <ShieldCheck className="h-4 w-4 group-hover:scale-110 transition-transform" />
                        <span className="font-bold">Protected by enterprise-grade encryption</span>
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) scale(1) rotate(0deg); }
          33% { transform: translate(80px, -80px) scale(1.15) rotate(5deg); }
          66% { transform: translate(40px, -120px) scale(1.1) rotate(-5deg); }
        }
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) scale(1) rotate(0deg); }
          33% { transform: translate(-90px, 90px) scale(1.25) rotate(-5deg); }
          66% { transform: translate(-60px, 120px) scale(1.2) rotate(5deg); }
        }
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(50px, -50px) scale(1.1); }
        }
        
        @keyframes floatPackage {
          0%, 100% { 
            transform: translate(0, 0) rotate(0deg) scale(1);
            opacity: 0.08;
          }
          25% { 
            transform: translate(15px, -30px) rotate(8deg) scale(1.1);
            opacity: 0.15;
          }
          50% { 
            transform: translate(40px, -60px) rotate(15deg) scale(1.05);
            opacity: 0.12;
          }
          75% { 
            transform: translate(25px, -40px) rotate(10deg) scale(1.08);
            opacity: 0.1;
          }
        }

        @keyframes truckDrive {
          0% {
            left: -10%;
            transform: translateY(0);
          }
          20% {
            transform: translateY(-5px);
          }
          40% {
            transform: translateY(0);
          }
          60% {
            transform: translateY(-3px);
          }
          80% {
            transform: translateY(0);
          }
          100% {
            left: 110%;
            transform: translateY(0);
          }
        }

        @keyframes exhaust {
          0% {
            transform: translate(0, 0) scale(1);
            opacity: 0.25;
          }
          100% {
            transform: translate(30px, -10px) scale(0);
            opacity: 0;
          }
        }

        @keyframes pinPulse {
          0%, 100% {
            transform: translateY(0) scale(1);
          }
          50% {
            transform: translateY(-8px) scale(1.1);
          }
        }

        @keyframes pingExpand {
          0% {
            transform: scale(1);
            opacity: 0.4;
          }
          100% {
            transform: scale(2.5);
            opacity: 0;
          }
        }

        @keyframes lineGlow {
          0%, 100% {
            opacity: 0.08;
          }
          50% {
            opacity: 0.25;
          }
        }

        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(200%);
          }
        }

        @keyframes dashMove {
          0% {
            stroke-dashoffset: 0;
          }
          100% {
            stroke-dashoffset: 1000;
          }
        }

        @keyframes pulse {
          0%, 100% {
            transform: scale(1);
            opacity: 1;
          }
          50% {
            transform: scale(1.1);
            opacity: 0.8;
          }
        }

        @keyframes spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }

        @keyframes ping-slow {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          75%, 100% {
            transform: scale(2);
            opacity: 0;
          }
        }

        @keyframes ping-slower {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          75%, 100% {
            transform: scale(2.5);
            opacity: 0;
          }
        }

        .animate-ping-slow {
          animation: ping-slow 2s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        .animate-ping-slower {
          animation: ping-slower 2.5s cubic-bezier(0, 0, 0.2, 1) infinite;
        }

        @keyframes gradient {
          0%, 100% {
            background-position: 0% 50%;
          }
          50% {
            background-position: 100% 50%;
          }
        }

        .animate-gradient {
          background-size: 200% 200%;
          animation: gradient 8s ease infinite;
        }
        
        @keyframes fade-in {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }

        @keyframes slide-in-from-top-2 {
          from {
            transform: translateY(-0.5rem);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }

        @keyframes zoom-in {
          from {
            transform: scale(0.95);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .animate-in {
          animation-fill-mode: both;
        }

        .fade-in {
          animation-name: fade-in;
        }

        .slide-in-from-top-2 {
          animation-name: slide-in-from-top-2;
        }

        .zoom-in {
          animation-name: zoom-in;
        }

        .duration-300 {
          animation-duration: 300ms;
        }

        .duration-500 {
          animation-duration: 500ms;
        }

        .duration-700 {
          animation-duration: 700ms;
        }
        
        input:-webkit-autofill,
        input:-webkit-autofill:hover,
        input:-webkit-autofill:focus,
        input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0px 1000px rgba(255,255,255,0.8) inset !important;
          -webkit-text-fill-color: #0f172a !important;
          caret-color: #0f172a !important;
          transition: background-color 99999s ease-in-out 0s;
        }

        input:-moz-autofill {
          box-shadow: 0 0 0px 1000px rgba(255,255,255,0.8) inset !important;
          -moz-text-fill-color: #0f172a !important;
          caret-color: #0f172a !important;
        }

        @media (prefers-reduced-motion: reduce) {
          *, *::before, *::after {
            animation-duration: 0.01ms !important;
            animation-iteration-count: 1 !important;
            transition-duration: 0.01ms !important;
          }
        }
      `}</style>
    </div>
  );
}